import React, { useState, useEffect } from 'react';
import axios from '../config/api';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { usePageContext } from '../context/PageContext';

const ExpenseRecipients = () => {
  const [recipients, setRecipients] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingRecipient, setEditingRecipient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedRecipientForPayment, setSelectedRecipientForPayment] = useState(null);
  const [paymentData, setPaymentData] = useState({ amount: '', paymentMethod: 'bank_transfer', reference: '', notes: '' });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    type: 'other',
    description: ''
  });
  const { hasPermission } = useAuth();
  const { setPageTitle } = usePageContext();

  useEffect(() => {
    setPageTitle('Expense Recipients');
    fetchRecipients();
  }, [setPageTitle]);

  const fetchRecipients = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/expense-recipient');
      setRecipients(response.data);
    } catch (error) {
      toast.error('Error fetching recipients');
      setRecipients([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    try {
      if (editingRecipient) {
        await axios.put(`/api/expense-recipient/${editingRecipient._id}`, formData);
        toast.success('Recipient updated successfully');
      } else {
        await axios.post('/api/expense-recipient', formData);
        toast.success('Recipient created successfully');
      }
      setShowModal(false);
      resetForm();
      setSelectedRecipients([]);
      fetchRecipients();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error saving recipient');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (recipientId) => {
    try {
      await axios.delete(`/api/expense-recipient/${recipientId}`);
      toast.success('Recipient deleted successfully');
      fetchRecipients();
    } catch (error) {
      toast.error('Error deleting recipient');
    }
  };

  const handleBulkDelete = async () => {
    try {
      await Promise.all(selectedRecipients.map(id => axios.delete(`/api/expense-recipient/${id}`)));
      toast.success(`${selectedRecipients.length} recipients deleted successfully`);
      setSelectedRecipients([]);
      setShowDeleteModal(false);
      fetchRecipients();
    } catch (error) {
      toast.error('Error deleting recipients');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', email: '', phoneNumber: '', type: 'other', description: '' });
    setEditingRecipient(null);
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    try {
      await axios.post('/api/payment', {
        recipient: selectedRecipientForPayment._id,
        ...paymentData,
        amount: parseFloat(paymentData.amount)
      });
      toast.success('Payment recorded successfully');
      setShowPaymentModal(false);
      setSelectedRecipientForPayment(null);
      setPaymentData({ amount: '', paymentMethod: 'bank_transfer', reference: '', notes: '' });
      fetchRecipients();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error recording payment');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (recipient) => {
    setEditingRecipient(recipient);
    setFormData({
      name: recipient.name,
      email: recipient.email,
      phoneNumber: recipient.phoneNumber || '',
      type: recipient.type,
      description: recipient.description || ''
    });
    setShowModal(true);
  };

  if (!hasPermission('expense', 'read')) {
    return <div className="alert alert-danger">Access denied</div>;
  }

  return (
    <div className="container-fluid">
      {/* Page Header with Info */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card bg-primary text-white">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h4 className="mb-2">💼 Expense Recipients</h4>
                  <p className="mb-0 opacity-75">Manage people and organizations who can receive expense payments from your company</p>
                  <small className="opacity-75">Add recipients → Submit expenses → Approve expenses → Make payments</small>
                </div>
                <div className="text-end">
                  <div className="h5 mb-0">{recipients.length} Recipients</div>
                  <small className="opacity-75">Total registered</small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Action Bar */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          {selectedRecipients.length > 0 && (
            <div className="d-flex align-items-center gap-2">
              <span className="badge bg-primary">{selectedRecipients.length} selected</span>
              <button className="btn btn-sm btn-danger" onClick={() => setShowDeleteModal(true)}>
                <i className="fas fa-trash me-1"></i>Delete Selected
              </button>
            </div>
          )}
        </div>
        <div className="d-flex gap-2">
          {hasPermission('expense', 'update') && (
            <button 
              className="btn btn-outline-secondary"
              onClick={async () => {
                try {
                  await axios.post('/api/expense-recipient/sync-totals');
                  toast.success('Recipient totals synced successfully');
                  fetchRecipients();
                } catch (error) {
                  toast.error('Error syncing totals');
                }
              }}
              title="Refresh all recipient totals from expenses and payments"
            >
              <i className="fas fa-sync me-2"></i>Sync Totals
            </button>
          )}
          {hasPermission('expense', 'create') && (
            <button className="btn btn-primary" onClick={() => setShowModal(true)}>
              <i className="fas fa-user-plus me-2"></i>Add Recipient
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2 text-muted">Loading recipients...</p>
        </div>
      ) : recipients.length === 0 ? (
        <div className="card">
          <div className="card-body text-center py-5">
            <div style={{ fontSize: '4rem', opacity: 0.3 }}>💼</div>
            <h5 className="text-muted mt-3">No expense recipients found</h5>
            <p className="text-muted mb-4">Start by adding people or organizations who can receive expense payments.</p>
            <div className="alert alert-info text-start">
              <h6><i className="fas fa-info-circle me-2"></i>Getting Started:</h6>
              <ol className="mb-0">
                <li>Add recipients (employees, vendors, contractors)</li>
                <li>Submit expenses for these recipients</li>
                <li>Approve expenses to create due amounts</li>
                <li>Make payments to settle due amounts</li>
              </ol>
            </div>
            {hasPermission('expense', 'create') && (
              <button className="btn btn-primary mt-3" onClick={() => setShowModal(true)}>
                <i className="fas fa-user-plus me-2"></i>Add First Recipient
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="card-header">
            <div className="d-flex justify-content-between align-items-center">
              <h6 className="mb-0">Recipients Overview</h6>
              <small className="text-muted">{recipients.length} total recipients</small>
            </div>
          </div>
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="table-light">
                <tr>
                  <th style={{width: '40px'}}>
                    <input
                      type="checkbox"
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedRecipients(recipients.map(r => r._id));
                        } else {
                          setSelectedRecipients([]);
                        }
                      }}
                      checked={selectedRecipients.length === recipients.length && recipients.length > 0}
                    />
                  </th>
                  <th>Recipient Details</th>
                  <th>Type</th>
                  <th>Total Approved</th>
                  <th>Amount Due</th>
                  <th>Total Paid</th>
                  <th>Status</th>
                  <th style={{width: '140px'}}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {recipients.map(recipient => (
                  <tr key={recipient._id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedRecipients.includes(recipient._id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedRecipients([...selectedRecipients, recipient._id]);
                          } else {
                            setSelectedRecipients(selectedRecipients.filter(id => id !== recipient._id));
                          }
                        }}
                      />
                    </td>
                    <td>
                      <div className="d-flex align-items-center">
                        <div className="avatar-circle me-3" style={{ width: '40px', height: '40px' }}>
                          {recipient.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div className="fw-semibold">{recipient.name}</div>
                          <small className="text-muted">{recipient.email}</small>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={`badge ${
                        recipient.type === 'employee' ? 'bg-primary' :
                        recipient.type === 'vendor' ? 'bg-success' :
                        recipient.type === 'contractor' ? 'bg-info' :
                        recipient.type === 'partner' ? 'bg-warning' : 'bg-secondary'
                      }`}>
                        {recipient.type.charAt(0).toUpperCase() + recipient.type.slice(1)}
                      </span>
                    </td>
                    <td>
                      <span className="fw-semibold text-success">${(recipient.totalApprovedExpenses || 0).toFixed(2)}</span>
                    </td>
                    <td>
                      <span className={`fw-bold ${(recipient.totalDueAmount || 0) > 0 ? 'text-danger' : 'text-muted'}`}>
                        ${(recipient.totalDueAmount || 0).toFixed(2)}
                      </span>
                    </td>
                    <td>
                      <span className="fw-semibold text-info">${(recipient.totalPaidAmount || 0).toFixed(2)}</span>
                    </td>
                    <td>
                      {(recipient.totalDueAmount || 0) > 0 ? (
                        <span className="badge bg-warning">Payment Due</span>
                      ) : (recipient.totalApprovedExpenses || 0) > 0 ? (
                        <span className="badge bg-success">Paid Up</span>
                      ) : (
                        <span className="badge bg-light text-dark">No Expenses</span>
                      )}
                    </td>
                    <td>
                      <div className="btn-group" role="group">
                        {(recipient.totalDueAmount || 0) > 0 && hasPermission('expense', 'approve') && (
                          <button 
                            className="btn btn-sm btn-success"
                            onClick={() => {
                              setSelectedRecipientForPayment(recipient);
                              setPaymentData({ amount: '', paymentMethod: 'bank_transfer', reference: '', notes: '' });
                              setShowPaymentModal(true);
                            }}
                            title={`Pay $${(recipient.totalDueAmount || 0).toFixed(2)}`}
                          >
                            <i className="fas fa-dollar-sign me-1"></i>Pay
                          </button>
                        )}
                        {hasPermission('expense', 'update') && (
                          <button 
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => handleEdit(recipient)}
                            title="Edit Recipient"
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                        )}
                        {hasPermission('expense', 'delete') && (
                          <button 
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDelete(recipient._id)}
                            title="Delete Recipient"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      {recipients.length > 0 && (
        <div className="row mt-4">
          <div className="col-md-3">
            <div className="card bg-light">
              <div className="card-body text-center">
                <h5 className="text-success">${recipients.reduce((sum, r) => sum + (r.totalApprovedExpenses || 0), 0).toFixed(2)}</h5>
                <small className="text-muted">Total Approved</small>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card bg-light">
              <div className="card-body text-center">
                <h5 className="text-danger">${recipients.reduce((sum, r) => sum + (r.totalDueAmount || 0), 0).toFixed(2)}</h5>
                <small className="text-muted">Total Due</small>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card bg-light">
              <div className="card-body text-center">
                <h5 className="text-info">${recipients.reduce((sum, r) => sum + (r.totalPaidAmount || 0), 0).toFixed(2)}</h5>
                <small className="text-muted">Total Paid</small>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card bg-light">
              <div className="card-body text-center">
                <h5 className="text-primary">{recipients.filter(r => (r.totalDueAmount || 0) > 0).length}</h5>
                <small className="text-muted">Need Payment</small>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingRecipient ? 'Edit' : 'Add'} Expense Recipient
                </h5>
                <button type="button" className="btn-close" onClick={() => { setShowModal(false); resetForm(); }}></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      className="form-control"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Phone Number</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.phoneNumber}
                      onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Type</label>
                    <select 
                      className="form-select"
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    >
                      <option value="employee">Employee</option>
                      <option value="vendor">Vendor</option>
                      <option value="contractor">Contractor</option>
                      <option value="partner">Partner</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-control"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows="2"
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => { setShowModal(false); resetForm(); }}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        {editingRecipient ? 'Updating...' : 'Creating...'}
                      </>
                    ) : (
                      `${editingRecipient ? 'Update' : 'Create'} Recipient`
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && selectedRecipientForPayment && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Make Payment to {selectedRecipientForPayment.name}</h5>
                <button type="button" className="btn-close" onClick={() => setShowPaymentModal(false)}></button>
              </div>
              <form onSubmit={handlePayment}>
                <div className="modal-body">
                  <div className="alert alert-info">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <strong>💰 Amount Due:</strong> ${(selectedRecipientForPayment.totalDueAmount || 0).toFixed(2)}
                      </div>
                      <small className="text-muted">Available for payment</small>
                    </div>
                  </div>
                  
                  <div className="mb-3">
                    <label className="form-label">Payment Amount</label>
                    <input
                      type="number"
                      step="0.01"
                      max={selectedRecipientForPayment.totalDueAmount || 0}
                      className="form-control"
                      value={paymentData.amount}
                      onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
                      required
                    />
                    <div className="form-text">
                      Maximum: ${(selectedRecipientForPayment.totalDueAmount || 0).toFixed(2)}
                    </div>
                  </div>
                  
                  <div className="mb-3">
                    <label className="form-label">Payment Method</label>
                    <select 
                      className="form-select"
                      value={paymentData.paymentMethod}
                      onChange={(e) => setPaymentData({ ...paymentData, paymentMethod: e.target.value })}
                    >
                      <option value="bank_transfer">Bank Transfer</option>
                      <option value="check">Check</option>
                      <option value="cash">Cash</option>
                      <option value="online">Online Payment</option>
                    </select>
                  </div>
                  
                  <div className="mb-3">
                    <label className="form-label">Reference Number</label>
                    <input
                      type="text"
                      className="form-control"
                      value={paymentData.reference}
                      onChange={(e) => setPaymentData({ ...paymentData, reference: e.target.value })}
                      placeholder="Transaction ID, Check number, etc."
                    />
                  </div>
                  
                  <div className="mb-3">
                    <label className="form-label">Notes</label>
                    <textarea
                      className="form-control"
                      value={paymentData.notes}
                      onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })}
                      rows="2"
                      placeholder="Additional notes about this payment"
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowPaymentModal(false)}>Cancel</button>
                  <button type="submit" className="btn btn-success" disabled={isSubmitting || !paymentData.amount}>
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Processing...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-dollar-sign me-2"></i>
                        Pay ${paymentData.amount || '0.00'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Delete</h5>
                <button type="button" className="btn-close" onClick={() => setShowDeleteModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="text-center">
                  <i className="fas fa-exclamation-triangle text-warning" style={{fontSize: '3rem'}}></i>
                  <h5 className="mt-3">Are you sure?</h5>
                  <p className="text-muted">
                    You are about to delete {selectedRecipients.length} recipient(s). This action cannot be undone.
                  </p>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>
                  Cancel
                </button>
                <button type="button" className="btn btn-danger" onClick={handleBulkDelete}>
                  <i className="fas fa-trash me-2"></i>Delete {selectedRecipients.length} Recipient(s)
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpenseRecipients;